import { FC, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { Card, ListGroup, Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import qs from 'query-string';

import API from 'core/Api';

import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';
import { useToast } from 'store/toast';

import { formatDate } from 'utils/helpers/formatters';
import { buildClientEntity } from 'utils/helpers/client';

import InnerWrapper from 'routes/client/reports/InnerWrapper';
import { getUserInfo } from 'utils/auth';
import { getSubdomain } from 'utils/helpers/routing';
import NotAuthorizedScreen from 'routes/not-authorized';
import ReportForm from './components/ReportForm';

interface PaidLeaveDaysCount {
  currentYear: number;
  previousYears: number;
}

interface AdditionalDataType {
  hasOverlappingLeavesForSameEmployee: boolean;
  leavesCount: number;
  manuallyAddedLeavesCount: number;
  paidLeaveDaysCount: PaidLeaveDaysCount;
  paidLeavesParagraph157Count: number;
  unpaidLeavesCount: number;
  employeesWithOverlappingLeaves: any[];
}

const LeavesReportsList: FC = () => {
  const methods = useForm();
  const openLinkBtn = useRef<HTMLAnchorElement>(null);
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { notify } = useToast();
  const isOperator = getSubdomain() === 'operators';
  const isLeaveModuleActive = getUserInfo().featureLeavesIsActive;
  const { t } = useTranslation();

  const [isLoadingXML, setIsLoadingXML] = useState(false);
  const [isLoadingAladdin, setIsLoadingAladdin] = useState(false);
  const [isLoadingBusinessNavigator, setIsLoadingBusinessNavigator] = useState(false);
  const [isLoadingStil, setIsLoadingStil] = useState(false);
  const [additionalData, setAdditionalData] = useState<AdditionalDataType | null>(null);
  const [link, setLink] = useState<string | null>(null);

  const {
    isLoading: isLoadingDropdowns,
    error: errorDropdowns,
    data: dropdowns,
  } = useQuery<any, Error>([buildClientEntity('report-leaves-list-dropdowns', client)], (): Promise<any> => {
    return API.get('reports/leaves-list/dropdown-options').then(res => {
      return res.data[language || 'bg'];
    });
  });

  const handleSubmitReport = (data: any) => {
    let dateFrom = data.date_from;
    let dateTo = data.date_to;

    // Check if any date is selected
    const shouldPopulateDates = !!dateFrom || !!dateTo;

    if (shouldPopulateDates) {
      dateFrom = data.date_from && formatDate(data.date_from, 'YYY/mm/dd');

      dateTo = data.date_to ? formatDate(data.date_to, 'YYY/mm/dd') : formatDate(new Date(), 'YYY/mm/dd');

      /**
       * In case any of the date fields (but not both) is empty on submit -
       * set value so that the time period is visible in filters
       */
      if (dateFrom) methods.setValue('date_from', new Date(dateFrom));
      if (dateTo) methods.setValue('date_to', new Date(dateTo));
    }

    const formattedData = {
      ...data,
      date_from: dateFrom,
      date_to: dateTo,
      user_id: data.user_id?.map((user: any) => user.value),
      group_id: data.group_id?.map((group: any) => group.value),
      type: data.type?.map((type: any) => type.value),
      status: data.status?.map((type: any) => type.value),
    };

    const queryString = qs.stringify(
      {
        ...formattedData,
      },
      { arrayFormat: 'bracket', skipNull: true }
    );
    return API.get(`reports/leaves-list/download?${queryString}`).then(res => {
      setLink(res.data.url);
    });
  };

  const handleDownloadXMLReport = (data: any) => {
    setIsLoadingXML(true);
    const formattedData = {
      ...data,
      date_from: data.date_from && formatDate(data.date_from, 'YYY/mm/dd'),
      date_to: data.date_to && formatDate(data.date_to, 'YYY/mm/dd'),
      user_id: data.user_id?.map((user: any) => user.value),
      group_id: data.group_id?.map((group: any) => group.value),
      type: data.type?.map((type: any) => type.value),
      status: data.status?.map((type: any) => type.value),
    };

    const queryString = qs.stringify(formattedData, { arrayFormat: 'bracket', skipNull: true });

    return API.get(`reports/leaves-xml/download?${queryString}`).then(res => {
      setLink(res.data.url);
      setAdditionalData(res.data);
      setIsLoadingXML(false);
    });
  };

  const handleDownloadAladdin = (data: any) => {
    setIsLoadingAladdin(true);
    const formattedData = {
      ...data,
      date_from: data.date_from && formatDate(data.date_from, 'YYY/mm/dd'),
      date_to: data.date_to && formatDate(data.date_to, 'YYY/mm/dd'),
      user_id: data.user_id?.map((user: any) => user.value),
      group_id: data.group_id?.map((group: any) => group.value),
      type: data.type?.map((type: any) => type.value),
      status: data.status?.map((type: any) => type.value),
    };

    const queryString = qs.stringify(formattedData, { arrayFormat: 'bracket', skipNull: true });

    return API.get(`reports/leaves-aladdin/download?${queryString}`)
      .then(res => {
        setLink(res.data.url);
        setIsLoadingAladdin(false);
      })
      .catch(() => setIsLoadingAladdin(false));
  };

  const handleDownloadBusinessNavigator = (data: any) => {
    notify({
      type: 'info',
      message: t('reports.businessNavigatorDownloadInfo'),
    });

    setIsLoadingBusinessNavigator(true);
    const formattedData = {
      ...data,
      date_from: data.date_from && formatDate(data.date_from, 'YYY/mm/dd'),
      date_to: data.date_to && formatDate(data.date_to, 'YYY/mm/dd'),
      user_id: data.user_id?.map((user: any) => user.value),
      group_id: data.group_id?.map((group: any) => group.value),
      type: data.type?.map((type: any) => type.value),
      status: data.status?.map((type: any) => type.value),
    };

    const queryString = qs.stringify(formattedData, { arrayFormat: 'bracket', skipNull: true });

    return API.get(`reports/leaves-business-navigator/download?${queryString}`)
      .then(res => {
        setLink(res.data.url);
        setIsLoadingBusinessNavigator(false);
      })
      .catch(() => setIsLoadingBusinessNavigator(false));
  };

  const handleDownloadStil = (data: any) => {
    setIsLoadingStil(true);
    const formattedData = {
      ...data,
      date_from: data.date_from && formatDate(data.date_from, 'YYY/mm/dd'),
      date_to: data.date_to && formatDate(data.date_to, 'YYY/mm/dd'),
      user_id: data.user_id?.map((user: any) => user.value),
      group_id: data.group_id?.map((group: any) => group.value),
      type: data.type?.map((type: any) => type.value),
      status: data.status?.map((type: any) => type.value),
    };

    const queryString = qs.stringify(formattedData, { arrayFormat: 'bracket', skipNull: true });

    return API.get(`reports/leaves-stil/download?${queryString}`)
      .then(res => {
        setLink(res.data.url);
        setIsLoadingStil(false);
      })
      .catch(() => setIsLoadingStil(false));
  };

  useEffect(() => {
    if (link) openLinkBtn?.current?.click();
  }, [link]);

  if (isLeaveModuleActive || isOperator) {
    return (
      <InnerWrapper
        isLoading={isLoadingDropdowns}
        error={errorDropdowns}
      >
        {dropdowns && (
          <ReportForm
            data={dropdowns}
            methods={methods}
            onSubmit={handleSubmitReport}
            onDownloadXML={handleDownloadXMLReport}
            onDownloadAladdin={handleDownloadAladdin}
            onDownloadBusinessNavigator={handleDownloadBusinessNavigator}
            onDownloadStil={handleDownloadStil}
            isLoadingXML={isLoadingXML}
            isLoadingAladdin={isLoadingAladdin}
            isLoadingBusinessNavigator={isLoadingBusinessNavigator}
            isLoadingStil={isLoadingStil}
          />
        )}
        {additionalData && (
          <Container className="d-flex mt-3">
            <Row className="m-auto align-self-center">
              <Col
                md={32}
                className="m-auto"
              >
                <Card>
                  <Card.Header as="h5">{t('reports.leaveStatistics')}</Card.Header>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <b>{t('reports.leavesCount')}</b>: {additionalData.leavesCount}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <b>{t('reports.paidleaveDaysCurr')}</b>: {additionalData.paidLeaveDaysCount.currentYear}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <b>{t('reports.paidleaveDaysPrev')}</b>: {additionalData.paidLeaveDaysCount.previousYears}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <b>{t('reports.unpaidLeaveDays')}</b>: {additionalData.unpaidLeavesCount}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <b>{t('reports.paidleaveDays157')}</b>: {additionalData.paidLeavesParagraph157Count}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <b>{t('reports.manuallyAdded')}</b>: {additionalData.manuallyAddedLeavesCount}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <b>{t('reports.hasOverlappingLeavesForSameEmployee')}</b>:{' '}
                      {additionalData.hasOverlappingLeavesForSameEmployee ? t('common.yes') : t('common.no')}
                    </ListGroup.Item>
                    {additionalData.hasOverlappingLeavesForSameEmployee && (
                      <ListGroup.Item>
                        <b>{t('reports.overlappingLeaves')}</b>:
                        <ListGroup>
                          {additionalData.employeesWithOverlappingLeaves.map((leave: any, key: any) => (
                            <ListGroup.Item key={key}>{leave}</ListGroup.Item>
                          ))}
                        </ListGroup>
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Card>
              </Col>
            </Row>
          </Container>
        )}
        {link && (
          <Link
            ref={openLinkBtn}
            to={{
              pathname: link,
            }}
            target="_blank"
            style={{ display: 'none' }}
          />
        )}
      </InnerWrapper>
    );
  }

  return <NotAuthorizedScreen />;
};

export default LeavesReportsList;
